/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import {
    Affix,
    Alert,
    Button,
    Form,
    Icon,
    Input,
    message,
    Modal,
    Row,
    Select,
    Table,
    Tabs,
    Tooltip,
} from 'antd';
import { FormComponentProps } from 'antd/es/form';
import { Moment } from 'moment';
import moment from 'moment';
import { errorHandle } from '../../services';
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
    SCHEDULE_STATUS,
    SCHEDULE_STATUS_OPTIONS,
    STICKY_ALERT_OFFSET_TOP,
} from './constants';
import {
    assertBizSuccess,
    cancelSchedule,
    createSchedule,
    fetchScheduleDetail,
    fetchSchedulePage,
    mapScheduleItem,
    pickAddMessage,
    pickPageResult,
    pickScheduleDetail,
} from './api';
import { ProductSelectItem } from './product-select-modal';
import { ScheduleCreateFormErrors, ScheduleCreateModal } from './schedule-create-modal';
import { ScheduleDetailModal } from './schedule-detail-modal';
import { ScheduleImportModal } from './schedule-import-modal';
import { ScheduleProductSelectModal } from './schedule-product-select-modal';

const { Option } = Select;
const { TabPane } = Tabs;

type DimType = 'SPU' | 'SKU';

const EMPTY_CREATE_ERRORS: ScheduleCreateFormErrors = {
    name: '',
    code: '',
    shelfTime: '',
    unshelfTime: '',
    remark: '',
};

const cleanParams = (params: Record<string, any>) => {
    const next = { ...params };
    Object.keys(next).forEach((key) => {
        if (next[key] === undefined || next[key] === null || next[key] === '') {
            delete next[key];
        }
    });
    return next;
};

const renderEllipsisCell = (text?: string) => {
    const value = text != null && String(text).trim() !== '' ? String(text).trim() : '-';
    if (value === '-') {
        return value;
    }
    return (
        <Tooltip title={value} placement="topLeft">
            <span className="shelf-rule__cell-ellipsis">{value}</span>
        </Tooltip>
    );
};

const formatScheduleTime = (value: Moment | null) =>
    value ? value.format('YYYY-MM-DD HH:mm') : undefined;

const ScheduleTabCom = ({ form }: FormComponentProps) => {
    const [dim, setDim] = useState<DimType>('SPU');
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchParams, setSearchParams] = useState<Record<string, any>>({});
    const [listData, setListData] = useState<any[]>([]);
    const [detailVisible, setDetailVisible] = useState(false);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailTask, setDetailTask] = useState<any>(null);
    const [detailItems, setDetailItems] = useState<any[]>([]);
    const [cancelRecord, setCancelRecord] = useState<any>(null);
    const [cancelSubmitting, setCancelSubmitting] = useState(false);
    const [importVisible, setImportVisible] = useState(false);

    const [createVisible, setCreateVisible] = useState(false);
    const [selectVisible, setSelectVisible] = useState(false);
    const [createName, setCreateName] = useState('');
    const [selectedItems, setSelectedItems] = useState<ProductSelectItem[]>([]);
    const [shelfTime, setShelfTime] = useState<Moment | null>(null);
    const [unshelfTime, setUnshelfTime] = useState<Moment | null>(null);
    const [remark, setRemark] = useState('');
    const [createErrors, setCreateErrors] = useState<ScheduleCreateFormErrors>(EMPTY_CREATE_ERRORS);
    const [createSubmitting, setCreateSubmitting] = useState(false);

    const fetchList = (query: Record<string, any> = {}) => {
        const merged = {
            dimension: dim,
            page: pageNum,
            per_page: pageSize,
            ...searchParams,
            ...query,
        };
        const payload = cleanParams(merged);
        setLoading(true);
        fetchSchedulePage(payload)
            .then((res: any) => {
                const { list, total: nextTotal } = pickPageResult(res);
                setListData(list.map(mapScheduleItem));
                setTotal(nextTotal);
                setPageNum(payload.page || 1);
                setPageSize(payload.per_page || pageSize);
            })
            .catch(errorHandle)
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchList({ page: 1, per_page: pageSize });
    }, []);

    const handleSearch = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFields((_err: any, values: any) => {
            const params = {
                name: values.name ? values.name.trim() : undefined,
                status: values.status,
                code: values.code ? values.code.trim() : undefined,
            };
            setSearchParams(params);
            fetchList({ ...params, page: 1, per_page: pageSize });
        });
    };

    const handleReset = () => {
        form.resetFields();
        setSearchParams({});
        fetchList({
            page: 1,
            per_page: pageSize,
            name: undefined,
            status: undefined,
            code: undefined,
        });
    };

    const openCancel = (record: any) => {
        setCancelRecord(record);
    };

    const closeCancel = () => {
        if (cancelSubmitting) {
            return;
        }
        setCancelRecord(null);
    };

    const confirmCancel = () => {
        if (!cancelRecord || !cancelRecord.id) {
            message.error('缺少任务信息，无法取消');
            return;
        }
        setCancelSubmitting(true);
        cancelSchedule(cancelRecord.id)
            .then((res: any) => {
                if (!assertBizSuccess(res)) {
                    message.error(pickAddMessage(res) || '取消失败');
                    return;
                }
                const resultMsg = pickAddMessage(res);
                message.success(resultMsg || '取消成功');
                setCancelRecord(null);
                fetchList({ page: pageNum, per_page: pageSize });
            })
            .catch(errorHandle)
            .finally(() => {
                setCancelSubmitting(false);
            });
    };

    const openImport = () => {
        setImportVisible(true);
    };

    const closeDetail = () => {
        setDetailVisible(false);
        setDetailTask(null);
        setDetailItems([]);
        setDetailLoading(false);
    };

    const openDetail = (record: any) => {
        if (!record || !record.id) {
            message.error('缺少任务信息，无法查看');
            return;
        }
        setDetailVisible(true);
        setDetailLoading(true);
        setDetailTask(null);
        setDetailItems([]);
        fetchScheduleDetail(record.id)
            .then((res: any) => {
                if (!assertBizSuccess(res)) {
                    message.error(pickAddMessage(res) || '获取详情失败');
                    closeDetail();
                    return;
                }
                const { task, items } = pickScheduleDetail(res);
                setDetailTask(task);
                setDetailItems(items);
            })
            .catch((err: any) => {
                closeDetail();
                errorHandle(err);
            })
            .finally(() => {
                setDetailLoading(false);
            });
    };

    const closeCreate = () => {
        setCreateVisible(false);
        setSelectVisible(false);
        setCreateName('');
        setSelectedItems([]);
        setShelfTime(null);
        setUnshelfTime(null);
        setRemark('');
        setCreateErrors(EMPTY_CREATE_ERRORS);
        setCreateSubmitting(false);
    };

    const openCreate = () => {
        setCreateName('');
        setSelectedItems([]);
        setShelfTime(null);
        setUnshelfTime(null);
        setRemark('');
        setCreateErrors(EMPTY_CREATE_ERRORS);
        setCreateVisible(true);
    };

    const validateCreateForm = () => {
        const nextErrors: ScheduleCreateFormErrors = { ...EMPTY_CREATE_ERRORS };
        const trimmedName = createName.trim();
        const now = moment();

        if (!trimmedName) {
            nextErrors.name = '请填写定时任务名称';
        } else if (trimmedName.length > 100) {
            nextErrors.name = '定时任务名称不能超过 100 字';
        }

        if (!selectedItems.length) {
            nextErrors.code = `请选择 ${dim}`;
        }

        if (!shelfTime && !unshelfTime) {
            nextErrors.shelfTime = '上架时间与下架时间至少填写一端';
            nextErrors.unshelfTime = '上架时间与下架时间至少填写一端';
        } else {
            if (shelfTime && shelfTime.isBefore(now)) {
                nextErrors.shelfTime = '计划时间不得早于当前';
            }
            if (unshelfTime && unshelfTime.isBefore(now)) {
                nextErrors.unshelfTime = '计划时间不得早于当前';
            }
            if (
                shelfTime &&
                unshelfTime &&
                !nextErrors.shelfTime &&
                !nextErrors.unshelfTime &&
                !unshelfTime.isAfter(shelfTime)
            ) {
                nextErrors.unshelfTime = '下架时间须晚于上架时间';
            }
        }

        if (remark.length > 200) {
            nextErrors.remark = '说明不能超过 200 字';
        }

        setCreateErrors(nextErrors);
        return !Object.values(nextErrors).some(Boolean);
    };

    const handleCreateOk = () => {
        if (!validateCreateForm()) {
            return;
        }
        const payload = {
            name: createName.trim(),
            dimension: dim,
            object_codes: selectedItems.map((item) => item.code),
            ...(shelfTime ? { shelf_time: formatScheduleTime(shelfTime) } : {}),
            ...(unshelfTime ? { unshelf_time: formatScheduleTime(unshelfTime) } : {}),
            ...(remark.trim() ? { remark: remark.trim() } : {}),
        };
        setCreateSubmitting(true);
        createSchedule(payload)
            .then((res: any) => {
                if (!assertBizSuccess(res)) {
                    message.error(pickAddMessage(res) || '创建失败');
                    return;
                }
                message.success(pickAddMessage(res) || '创建成功');
                closeCreate();
                fetchList({ page: 1, per_page: pageSize });
            })
            .catch(errorHandle)
            .finally(() => {
                setCreateSubmitting(false);
            });
    };

    const handleSelectOk = (items: ProductSelectItem[]) => {
        setSelectedItems(items);
        setSelectVisible(false);
        if (items.length) {
            setCreateErrors((prev) => ({ ...prev, code: '' }));
        }
    };

    const handleDimChange = (key: string) => {
        const nextDim = key as DimType;
        setDim(nextDim);
        setPageNum(1);
        form.resetFields();
        setSearchParams({});
        setDetailVisible(false);
        setDetailTask(null);
        setDetailItems([]);
        setCancelRecord(null);
        setImportVisible(false);
        closeCreate();
        fetchList({
            dimension: nextDim,
            page: 1,
            per_page: pageSize,
            name: undefined,
            status: undefined,
            code: undefined,
        });
    };

    const canCancel = (statusCode?: string, status?: string) => {
        if (statusCode) {
            return (
                statusCode === SCHEDULE_STATUS.WAIT_START ||
                statusCode === SCHEDULE_STATUS.RUNNING ||
                statusCode === SCHEDULE_STATUS.PARTIAL_FAIL
            );
        }
        return status === '待开始' || status === '进行中' || status === '部分失败';
    };

    const columns: any[] = [
        {
            title: '定时任务名称',
            dataIndex: 'name',
            key: 'name',
            width: 180,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: `${dim}摘要`,
            dataIndex: 'remark',
            key: 'remark',
            width: 240,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '上架时间',
            dataIndex: 'onTime',
            key: 'onTime',
            width: 180,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '下架时间',
            dataIndex: 'offTime',
            key: 'offTime',
            width: 180,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '创建人',
            dataIndex: 'creator',
            key: 'creator',
            width: 160,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 180,
            className: 'shelf-rule__ellipsis-col',
            render: (text: string) => renderEllipsisCell(text),
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (_: any, record: any) => (
                <span className="shelf-rule__actions">
                    <a onClick={() => openDetail(record)}>查看</a>
                    {canCancel(record.statusCode, record.status) && (
                        <a onClick={() => openCancel(record)}>取消</a>
                    )}
                </span>
            ),
        },
    ];

    return (
        <div>
            <Affix offsetTop={STICKY_ALERT_OFFSET_TOP}>
                <div className="shelf-rule__sticky-alert">
                    <Alert
                        type="warning"
                        showIcon
                        message="定时上下架说明"
                        description="一条定时任务对应一组同维度商品，以及上架/下架时间。创建后，商品会写入禁止上架名单（来源「活动定时」）。到点上架：移出本任务相关的名单记录并重算，满足自动上架条件则上架。到点下架：写入禁止上架名单并立即下架。取消未结束的任务时，未执行的到点动作不再执行，并删除本任务写入的名单记录。上架时间与下架时间至少填一端。"
                    />
                </div>
            </Affix>
            <Tabs
                activeKey={dim}
                onChange={handleDimChange}
                animated={{ inkBar: true, tabPane: false }}
            >
                <TabPane tab="SPU" key="SPU" />
                <TabPane tab="SKU" key="SKU" />
            </Tabs>
            <Form layout="inline" onSubmit={handleSearch} className="shelf-rule__filter">
                <Form.Item label="定时任务名称">
                    {form.getFieldDecorator('name')(
                        <Input placeholder="名称" style={{ width: 180 }} />
                    )}
                </Form.Item>
                <Form.Item label="状态">
                    {form.getFieldDecorator('status')(
                        <Select allowClear placeholder="全部" style={{ width: 140 }}>
                            {SCHEDULE_STATUS_OPTIONS.map((item) => (
                                <Option key={item.value} value={item.value}>
                                    {item.label}
                                </Option>
                            ))}
                        </Select>
                    )}
                </Form.Item>
                <Form.Item label={`${dim}编码`}>
                    {form.getFieldDecorator('code')(
                        <Input placeholder={`${dim}编码`} style={{ width: 180 }} />
                    )}
                </Form.Item>
                <Form.Item>
                    <Button type="primary" htmlType="submit">
                        查询
                    </Button>
                    <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                        重置
                    </Button>
                </Form.Item>
            </Form>
            <Row style={{ marginBottom: 16 }}>
                <Button type="primary" onClick={openCreate}>
                    创建
                </Button>
                <Button style={{ marginLeft: 8 }} onClick={openImport}>
                    批量导入
                </Button>
            </Row>
            <ScheduleImportModal
                visible={importVisible}
                dim={dim}
                onCancel={() => setImportVisible(false)}
                onSuccess={() => fetchList({ page: 1, per_page: pageSize })}
            />
            <ScheduleCreateModal
                dim={dim}
                visible={createVisible}
                name={createName}
                selectedItems={selectedItems}
                shelfTime={shelfTime}
                unshelfTime={unshelfTime}
                remark={remark}
                errors={createErrors}
                confirmLoading={createSubmitting}
                onCancel={closeCreate}
                onOk={handleCreateOk}
                onOpenSelect={() => setSelectVisible(true)}
                onRemoveSelected={(code) =>
                    setSelectedItems((prev) => prev.filter((item) => item.code !== code))
                }
                onNameChange={(value) => {
                    setCreateName(value);
                    if (value.trim()) {
                        setCreateErrors((prev) => ({ ...prev, name: '' }));
                    }
                }}
                onShelfTimeChange={(value) => {
                    setShelfTime(value);
                    setCreateErrors((prev) => ({
                        ...prev,
                        shelfTime: '',
                        unshelfTime:
                            prev.unshelfTime === '上架时间与下架时间至少填写一端'
                                ? ''
                                : prev.unshelfTime,
                    }));
                }}
                onUnshelfTimeChange={(value) => {
                    setUnshelfTime(value);
                    setCreateErrors((prev) => ({
                        ...prev,
                        unshelfTime: '',
                        shelfTime:
                            prev.shelfTime === '上架时间与下架时间至少填写一端'
                                ? ''
                                : prev.shelfTime,
                    }));
                }}
                onRemarkChange={(value) => {
                    setRemark(value);
                    if (value.length <= 200) {
                        setCreateErrors((prev) => ({ ...prev, remark: '' }));
                    }
                }}
            />
            <ScheduleProductSelectModal
                key={`schedule-${dim}`}
                visible={selectVisible}
                dim={dim}
                defaultSelected={selectedItems}
                onCancel={() => setSelectVisible(false)}
                onOk={handleSelectOk}
            />
            <Table
                className="shelf-rule__table"
                rowKey="id"
                loading={loading}
                columns={columns}
                dataSource={listData}
                pagination={{
                    total,
                    current: pageNum,
                    pageSize,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    pageSizeOptions: PAGE_SIZE_OPTIONS,
                    showTotal: (count) => `共 ${count} 条记录`,
                    onChange: (page) => {
                        fetchList({ page, per_page: pageSize });
                    },
                    onShowSizeChange: (_current, size) => {
                        fetchList({ page: 1, per_page: size });
                    },
                }}
            />
            <ScheduleDetailModal
                visible={detailVisible}
                loading={detailLoading}
                task={detailTask}
                items={detailItems}
                onCancel={closeDetail}
            />
            <Modal
                title="取消确认"
                visible={!!cancelRecord}
                onCancel={closeCancel}
                onOk={confirmCancel}
                confirmLoading={cancelSubmitting}
                okText="确定"
                cancelText="取消"
                destroyOnClose
            >
                <div className="shelf-rule__delete-confirm">
                    <Icon
                        type="exclamation-circle"
                        theme="filled"
                        style={{ color: '#faad14', fontSize: 22 }}
                        className="shelf-rule__delete-confirm-icon"
                    />
                    <div className="shelf-rule__delete-confirm-text">
                        {cancelRecord
                            ? `确定取消定时任务「${cancelRecord.name ||
                                  ''}」？未执行的到点动作将不再执行，本任务写入的禁止上架名单也会删除。`
                            : ''}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export const ScheduleTab = Form.create()(ScheduleTabCom);
